import type {
  GenericWidgetSemantics,
  WidgetSemantics,
} from "@src/types/dashboards";
import { parseUserNumber } from "./utils/parse-user-number";

/** Resolved manual Y-axis bounds (engineering units), or null for automatic. */
export interface AxisBounds {
  min: number;
  max: number;
}

/**
 * Both-or-neither manual bounds. Returns the pair only when both ends are
 * present, finite and `min < max`; otherwise the chart keeps its automatic
 * range. Zero is a legitimate bound (nullish, not truthiness, is checked).
 * Rejected here as well as in the editor so an invalid persisted config can
 * never produce a reversed or NaN axis.
 */
export const parseManualBounds = (
  yMin: number | null | undefined,
  yMax: number | null | undefined,
): AxisBounds | null => {
  if (yMin == null || yMax == null) return null;
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) return null;
  if (yMin >= yMax) return null;
  return { min: yMin, max: yMax };
};

/** Read the manual bounds a line chart stored on its semantics. */
export const readAxisBounds = (
  semantics: WidgetSemantics | null | undefined,
): AxisBounds | null => {
  const s = semantics as Partial<GenericWidgetSemantics> | null | undefined;
  return parseManualBounds(s?.min_value, s?.max_value);
};

/** Why a manual-Y-range input pair is not saveable (for the editor's i18n). */
export type AxisBoundsInputError = "required" | "invalid";

/**
 * Validate the two `Y min` / `Y max` inputs. Both are required, must parse
 * (comma decimals accepted) and `min < max`. Returns null when saveable.
 */
export const axisBoundsInputError = (
  yMinText: string,
  yMaxText: string,
): AxisBoundsInputError | null => {
  const min = parseUserNumber(yMinText);
  const max = parseUserNumber(yMaxText);
  if (min == null || max == null) return "required";
  if (min >= max) return "invalid";
  return null;
};
